import { Button } from '@/components/ui/button';
import { IconChat, IconCommand, IconSend, IconSpinner } from './icons';
import { useEnterSubmit } from './hooks/use-enter-submit';
import * as React from 'react';
import Textarea from 'react-textarea-autosize';
import { CustomStylesI } from '.';
import { useIsDarkMode } from './hooks/use-is-dark-mode';

export function PromptForm({
	onSubmit,
	input,
	setInput,
	isLoading,
	reset,
	customStyles
}: {
	onSubmit: (e: React.FormEvent<HTMLFormElement>) => Promise<void>;
	input: string;
	setInput: (
		e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
	) => void;
	isLoading: boolean;
	reset: () => void;
	customStyles?: CustomStylesI;
}) {
	const { formRef, onKeyDown } = useEnterSubmit();
	const inputRef = React.useRef<HTMLTextAreaElement>(null);
	const isDarkMode = useIsDarkMode();

	React.useEffect(() => {
		if (inputRef.current) {
			inputRef.current.focus();
		}
	}, []);

	return (
		<form
			onSubmit={e => {
				e.preventDefault();
				if (!isLoading) onSubmit(e);
			}}
			ref={formRef}
		>
			<div
				className="lb-relative lb-flex lb-max-h-60 lb-w-full lb-grow lb-flex-col lb-overflow-hidden lb-bg-background lb-px-2 lb-pb-2 sm:lb-rounded-2xl sm:lb-border sm:lb-border-border sm:lb-border-solid"
				style={{
					...(customStyles?.chatbot?.backgroundColor && {
						backgroundColor: !isDarkMode
							? customStyles?.chatbot?.backgroundColor
							: customStyles?.chatbot?.foregroundColor
					}),
					...(customStyles?.chatbot?.foregroundColor && {
						borderColor: !isDarkMode
							? customStyles?.chatbot?.foregroundColor
							: customStyles?.chatbot?.backgroundColor
					})
				}}
			>
				<div className="lb-flex lb-w-full lb-flex-col">
					<label
						htmlFor="playground"
						className="lb-text-config lb-flex lb-justify-between lb-gap-y-4 lb-rounded-xl lb-px-3 lb-py-4 lb-font-medium lb-leading-6 lb-text-foreground md:lb-flex-row md:lb-items-center md:lb-gap-y-0"
					>
						<div className="lb-flex lb-items-center lb-gap-x-2">
							<IconChat
								className="lb-size-5 lb-text-muted-foreground/50"
								aria-hidden="true"
								style={{
									...(customStyles?.chatbot
										?.foregroundColor && {
										color: !isDarkMode
											? customStyles?.chatbot
													?.foregroundColor
											: customStyles?.chatbot
													?.backgroundColor,

										opacity: 0.5
									})
								}}
							/>
							<h3
								className="lb-text-md lb-font-semibold"
								style={{
									...(customStyles?.chatbot
										?.foregroundColor && {
										color: !isDarkMode
											? customStyles?.chatbot
													?.foregroundColor
											: customStyles?.chatbot
													?.backgroundColor
									})
								}}
							>
								Ask
							</h3>
						</div>

						<div className="lb-flex lb-items-center lb-justify-center lb-gap-2 md:lb-justify-start">
							<Button
								variant="ghost"
								className="lb-max-w-xs lb-text-muted-foreground/70"
								onClick={reset}
								style={{
									...(customStyles?.chatbot
										?.foregroundColor && {
										color: !isDarkMode
											? customStyles?.chatbot
													?.foregroundColor
											: customStyles?.chatbot
													?.backgroundColor
									}),
								}}
							>
								Clear
							</Button>
							<Button
								type="submit"
								className="lb-flex lb-items-center lb-gap-2"
								disabled={isLoading || input === ''}
								style={{
									...(customStyles?.chatbot
										?.foregroundColor && {
										color: !isDarkMode
											? customStyles?.chatbot
													?.backgroundColor
											: customStyles?.chatbot
													?.foregroundColor
									}),
									...(customStyles?.chatbot
										?.backgroundColor && {
										backgroundColor: !isDarkMode
											? customStyles?.chatbot
													?.foregroundColor
											: customStyles?.chatbot
													?.backgroundColor
									})
								}}
							>
								{isLoading ? (
									<>
										<IconSpinner className="lb-size-4" />
										Loading
									</>
								) : (
									<>
										<IconSend className="lb-size-4" />
										Send
									</>
								)}

								<span className="lb-sr-only">Send message</span>
							</Button>
						</div>
					</label>
				</div>
				<Textarea
					ref={inputRef}
					tabIndex={0}
					onKeyDown={onKeyDown}
					rows={1}
					maxRows={10}
					value={input}
					onChange={setInput}
					placeholder="Enter your question..."
					spellCheck={false}
					className="lb-min-h-[60px] lb-w-full lb-resize-none lb-rounded-lg lb-bg-muted lb-px-4 lb-py-[1.3rem] focus-within:lb-outline-none focus-within:lb-ring-0 sm:lb-text-sm focus-visible:lb-ring-0 focus-visible:lb-outline-none focus:lb-ring-0 focus:lb-outline-none lb-outline-none lb-border-none lb-font-sans placeholder:lb-text-muted-foreground"
				/>
			</div>
		</form>
	);
}
